import React from 'react';
import { Check, ChevronRight, Inbox, X } from 'lucide-react';
import { EntityType, PendingEntryStatus, WorkflowQuery } from '../../types/workflow';
import { Button, Card, EmptyState, EntityIcon, SegmentedControl, Select, Skeleton, entityKindOf } from '../ui';
import { formatRelative, isReviewable, Pager, StatusPill } from './workflowFormat';

export interface QueueRow {
  id: string;
  name: string;
  typeLabel: string;
  /** Raw entity type (`stakeholder`, `wiki_page`, …) for the kind icon */
  entityType?: string;
  /** Extra meta after the type, e.g. "3 fields" */
  extra?: string;
  by?: string;
  at: string;
  assignee?: string;
  status: PendingEntryStatus;
}

export type QuickDecision = PendingEntryStatus.APPROVED | PendingEntryStatus.REJECTED;

interface ReviewQueueProps {
  rows: QueueRow[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  showFilters: boolean;
  filters: WorkflowQuery;
  onFilterChange: (filters: Partial<WorkflowQuery>) => void;
  pagination: { page: number; limit: number; total: number; totalPages: number };
  onPageChange: (page: number) => void;
  onOpen: (id: string) => void;
  onDecide: (id: string, status: QuickDecision) => void;
  /** `${id}:${status}` of the decision in flight */
  acting: string | null;
  actionError: string | null;
  emptyLabel: string;
  byLabel: string;
}

type StatusFilter = PendingEntryStatus | 'all';

const STATUS_ITEMS: Array<{ value: StatusFilter; label: string }> = [
  { value: PendingEntryStatus.PENDING, label: 'Pending' },
  { value: PendingEntryStatus.UNDER_REVIEW, label: 'In review' },
  { value: PendingEntryStatus.APPROVED, label: 'Approved' },
  { value: PendingEntryStatus.REJECTED, label: 'Rejected' },
  { value: 'all', label: 'All' },
];

/** Shared filters + rows for pending entries and change proposals. */
export const ReviewQueue: React.FC<ReviewQueueProps> = ({
  rows,
  loading,
  error,
  onRetry,
  showFilters,
  filters,
  onFilterChange,
  pagination,
  onPageChange,
  onOpen,
  onDecide,
  acting,
  actionError,
  emptyLabel,
  byLabel,
}) => {
  const busy = (id: string) => !!acting && acting.startsWith(`${id}:`);

  const actions = (row: QueueRow, phone = false) =>
    isReviewable(row.status) ? (
      <div
        className={`flex items-center gap-1.5 ${phone ? 'mt-2' : 'justify-end'}`}
        onClick={e => e.stopPropagation()}
      >
        <Button
          size={phone ? 'sm' : 'xs'}
          variant="secondary"
          onClick={() => onDecide(row.id, PendingEntryStatus.REJECTED)}
          loading={acting === `${row.id}:${PendingEntryStatus.REJECTED}`}
          disabled={busy(row.id)}
          className={phone ? 'flex-1' : ''}
        >
          {acting !== `${row.id}:${PendingEntryStatus.REJECTED}` && <X className="h-3.5 w-3.5" strokeWidth={2} />}
          Reject
        </Button>
        <Button
          size={phone ? 'sm' : 'xs'}
          variant="primary"
          onClick={() => onDecide(row.id, PendingEntryStatus.APPROVED)}
          loading={acting === `${row.id}:${PendingEntryStatus.APPROVED}`}
          disabled={busy(row.id)}
          className={phone ? 'flex-1' : ''}
        >
          {acting !== `${row.id}:${PendingEntryStatus.APPROVED}` && <Check className="h-3.5 w-3.5" strokeWidth={2} />}
          Approve
        </Button>
      </div>
    ) : phone ? null : (
      <div className="flex justify-end">
        <ChevronRight className="h-4 w-4 text-neutral-300" strokeWidth={1.75} />
      </div>
    );

  const meta = (row: QueueRow) => [row.typeLabel, row.extra].filter(Boolean).join(' · ');

  /** Kind tile so mixed queues scan at a glance */
  const kindIcon = (row: QueueRow, className = '') => {
    const kind = entityKindOf(row.entityType);
    return kind ? <EntityIcon kind={kind} size="sm" className={className} /> : null;
  };

  return (
    <div>
      {showFilters && (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <SegmentedControl<StatusFilter>
            label="Status"
            items={STATUS_ITEMS}
            value={filters.status || 'all'}
            onChange={value => onFilterChange({ status: value === 'all' ? undefined : value })}
          />
          <Select
            aria-label="Entity type"
            className="min-w-0 flex-1 sm:w-40 sm:flex-none"
            value={filters.entityType || ''}
            onChange={e => onFilterChange({ entityType: (e.target.value || undefined) as EntityType | undefined })}
          >
            <option value="">All types</option>
            <option value={EntityType.STAKEHOLDER}>Stakeholder</option>
            <option value={EntityType.ORGANIZATION}>Organization</option>
            <option value={EntityType.ASSIGNMENT}>Assignment</option>
            <option value={EntityType.WIKI_PAGE}>Wiki Page</option>
          </Select>
          <Select
            aria-label="Items per page"
            className="hidden w-[92px] sm:ml-auto sm:block"
            value={filters.limit || 10}
            onChange={e => onFilterChange({ limit: parseInt(e.target.value) })}
          >
            {[5, 10, 20, 50].map(n => (
              <option key={n} value={n}>
                {n} rows
              </option>
            ))}
          </Select>
        </div>
      )}

      {actionError && (
        <p role="alert" className="mb-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {actionError}
        </p>
      )}

      <Card clip>
        {loading ? (
          <div role="status" aria-label="Loading" className="divide-y divide-neutral-100">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-44" />
                  <Skeleton className="h-3 w-24" />
                </div>
                <Skeleton className="hidden h-3.5 w-32 sm:block" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-wrap items-center justify-between gap-3 p-4">
            <p role="alert" className="text-[13px] text-primary-700">
              {error}
            </p>
            <Button size="sm" variant="secondary" onClick={onRetry}>
              Retry
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Inbox} title={emptyLabel} />
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-x-auto sm:block">
              <table className="w-full border-collapse text-left text-[13px]">
                <thead className="border-b border-neutral-200/70">
                  <tr>
                    <th className="h-9 px-4 text-[12px] font-normal text-neutral-400">Entity</th>
                    <th className="h-9 px-4 text-[12px] font-normal text-neutral-400">{byLabel}</th>
                    <th className="h-9 px-4 text-[12px] font-normal text-neutral-400">When</th>
                    <th className="hidden h-9 px-4 text-[12px] font-normal text-neutral-400 xl:table-cell">Assigned</th>
                    <th className="h-9 px-4 text-[12px] font-normal text-neutral-400">Status</th>
                    <th className="h-9 px-4">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100">
                  {rows.map(row => (
                    <tr
                      key={row.id}
                      onClick={() => onOpen(row.id)}
                      className="cursor-pointer transition-colors hover:bg-neutral-50"
                    >
                      <td className="h-12 max-w-[320px] px-4 py-1.5">
                        <div className="flex min-w-0 items-center gap-3">
                          {kindIcon(row)}
                          <div className="min-w-0">
                            <p className="truncate text-ink">{row.name}</p>
                            <p className="truncate text-[12px] text-neutral-400">{meta(row)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="max-w-[220px] truncate px-4 py-2 text-neutral-600">{row.by || 'Unknown'}</td>
                      <td
                        className="whitespace-nowrap px-4 py-2 tabular-nums text-neutral-500"
                        title={new Date(row.at).toLocaleString()}
                      >
                        {formatRelative(row.at)}
                      </td>
                      <td className="hidden max-w-[200px] truncate px-4 py-2 text-neutral-500 xl:table-cell">
                        {row.assignee || <span className="text-neutral-300">—</span>}
                      </td>
                      <td className="px-4 py-2">
                        <StatusPill status={row.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-2">{actions(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Phone rows */}
            <ul className="divide-y divide-neutral-200/70 sm:hidden">
              {rows.map(row => (
                <li key={row.id} className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => onOpen(row.id)}
                    className="flex w-full items-start gap-3 text-left"
                  >
                    {kindIcon(row, 'mt-0.5')}
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] text-ink">{row.name}</span>
                      <span className="mt-0.5 block truncate text-[12px] text-neutral-400">
                        {meta(row)} · {row.by?.split('@')[0] || 'Unknown'} · {formatRelative(row.at)}
                      </span>
                    </span>
                    <StatusPill status={row.status} />
                  </button>
                  {actions(row, true)}
                </li>
              ))}
            </ul>

            <Pager {...pagination} onPageChange={onPageChange} />
          </>
        )}
      </Card>
    </div>
  );
};
